import { useEffect } from "react";
import {
  useSectionCompletionStore,
  sectionKey,
} from "@/stores/sectionCompletionStore";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useCourseGlossaryStore } from "@/stores/courseGlossaryStore";
import { stripThirdLevelNumbering } from "@/lib/courseUtils";
import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";
import type { Storyboard } from "@/types/storyboard";
import type { GlossaryTerm } from "@/types/glossary";

interface ChapterIntroContent {
  title: string;
  body: string;
  contentTitle?: string;
  contentElements: CourseTreeNode[];
  storyboards: Storyboard[];
  storyboardsLoading: boolean;
  glossaryTerms: GlossaryTerm[];
  onContentSeen: () => void;
}

/**
 * Prefetches first-subchapter storyboards and builds props for the chapter intro.
 */
const useChapterIntroContent = (
  chapter: CourseTreeNode,
  introFallback: string
): ChapterIntroContent => {
  const markCompleted = useSectionCompletionStore(
    (state) => state.markCompleted
  );
  const nodeIndex = useCourseDetailStore((state) => state.nodeIndex);
  const fetchStoryboardsBySubChapter = useCourseDetailStore(
    (state) => state.fetchStoryboardsBySubChapter
  );
  const storyboards = useCourseDetailStore((state) => state.storyboards);
  const storyboardsLoading = useCourseDetailStore(
    (state) => state.storyboardsLoading
  );
  const storyboardSubChapterId = useCourseDetailStore(
    (state) => state.storyboardSubChapterId
  );
  const glossaryTerms = useCourseGlossaryStore((state) => state.terms);

  const introText = chapter.intro || introFallback;

  const firstSubchapter = chapter.children?.find(
    (child) => child.type === CourseTreeNodeTypeEnum.Subchapter
  );
  const firstSubchapterId = firstSubchapter?.id;

  const contentElements = firstSubchapterId
    ? (nodeIndex.get(firstSubchapterId)?.contentElements ?? [])
    : [];

  const contentReady =
    firstSubchapterId != null && storyboardSubChapterId === firstSubchapterId;

  useEffect(() => {
    if (!firstSubchapterId) return;
    void fetchStoryboardsBySubChapter(firstSubchapterId);
  }, [firstSubchapterId, fetchStoryboardsBySubChapter]);

  return {
    title: chapter.title,
    body: introText,
    contentTitle: firstSubchapter
      ? stripThirdLevelNumbering(firstSubchapter.title)
      : undefined,
    contentElements,
    storyboards: contentReady ? storyboards : [],
    storyboardsLoading: !contentReady || storyboardsLoading,
    glossaryTerms,
    onContentSeen: () => markCompleted(sectionKey("intro", chapter.id)),
  };
};

export { useChapterIntroContent };
